import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import allApi from "../../../../api/allApi";
import errorIcon from "../../../../assets/images/error-2.png";
import Loader from "../../common/Loader";
import NmsTableSearch, { searchRows, sortRows } from "../../common/NmsTableSearch";
import { useLocalization } from "../../localization/LocalizationContext";
import { useNetwork } from "../../common/networkContext";

const PAGE_SIZE = 10;
const VLAN_FIELDS = ["vlan_name", "vlan_id", "description", "network_type"];

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.response)) return value.response;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

function VlanNetworkSetup() {
  const navigate = useNavigate();
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;
  const [vlanList, setVlanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [vlanId, setVlanId] = useState("");
  const [tableQuery, setTableQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [page, setPage] = useState(1);

  const fetchVlanList = async (id = networkId, { silent = false } = {}) => {
    if (!id) {
      setVlanList([]);
      setLoading(false);
      return;
    }
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);
      const api = new allApi();
      const response = await api.getRequest(`api/vlan/list?networkId=${id}`);
      const list = asList(response).sort((a, b) => {
        const dateA = new Date(a.lastUpdated || a.createdAt || 0);
        const dateB = new Date(b.lastUpdated || b.createdAt || 0);
        return dateB - dateA;
      });
      setVlanList(list);
    } catch (error) {
      console.error("Failed to fetch VLAN list:", error);
      setVlanList([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchVlanList(networkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId]);

  const visible = useMemo(
    () =>
      sortRows(
        searchRows(vlanList, tableQuery, VLAN_FIELDS),
        sortConfig.key,
        sortConfig.direction
      ),
    [vlanList, tableQuery, sortConfig]
  );

  useEffect(() => {
    setPage(1);
  }, [tableQuery, sortConfig.key, sortConfig.direction]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const deleteVlan = async (id) => {
    try {
      setRefreshing(true);
      const api = new allApi();
      await api.deleteRequest(`api/vlan/del/vlan/${id}`);
      await fetchVlanList(networkId, { silent: true });
    } catch (error) {
      console.error("Failed to delete VLAN", error);
      setRefreshing(false);
    }
  };

  const handleEditVlan = async (id) => {
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/vlan/${id}`);
      navigate("/configureNetwork", {
        state: {
          editVlanId: id,
          vlanDetails: response?.response || response,
        },
      });
    } catch (error) {
      console.error("Failed to fetch VLAN details:", error);
    }
  };

  const toggleSort = (field) => {
    setSortConfig((prev) => ({
      key: field,
      direction: prev.key === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortHeader = (field, label) => (
    <th
      className={`inc-th-sort${sortConfig.key === field ? " is-active" : ""}`}
      onClick={() => toggleSort(field)}
    >
      {label}
      <i className="fa fa-sort" aria-hidden="true" />
    </th>
  );

  return (
    <div>
      <div className="config-list-toolbar">
        <NmsTableSearch
          value={tableQuery}
          onChange={setTableQuery}
          placeholder={localizationStrings?.search || "Search VLANs"}
        />
        <button className="icon-btn" title="Refresh" onClick={() => fetchVlanList(networkId, { silent: true })}>
          <i className={`fa ${refreshing ? "fa-spinner fa-spin" : "fa-refresh"}`} aria-hidden="true" />
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="inc-card-table">
            <table className="full-table m-head">
              <thead>
                <tr>
                  {sortHeader("vlan_name", localizationStrings?.vlanName)}
                  {sortHeader("vlan_id", localizationStrings?.vlanId)}
                  {sortHeader("description", localizationStrings?.description)}
                  {sortHeader("network_type", localizationStrings?.networkType)}
                  <th>{localizationStrings?.action}</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length ? (
                  pageRows.map((vlan) => (
                    <tr key={vlan.vlan_id || vlan.vlan_name}>
                      <td>{vlan.vlan_name || "N/A"}</td>
                      <td>{vlan.vlan_id || "N/A"}</td>
                      <td>{vlan.description && vlan.description !== "N/A" ? vlan.description : "—"}</td>
                      <td>
                        <span className="config-type-chip">
                          {vlan.network_type || "N/A"}
                        </span>
                      </td>
                      <td>
                        <div className="button-group">
                          <button
                            className="icon-btn"
                            title="Edit"
                            onClick={() => handleEditVlan(vlan.vlan_id)}
                          >
                            <i className="fa fa-edit" aria-hidden="true" />
                          </button>
                          <button
                            className="icon-btn"
                            title="Delete"
                            data-bs-toggle="modal"
                            data-bs-target="#deleteVlan"
                            onClick={() => setVlanId(vlan.vlan_id)}
                          >
                            <i className="fa fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">
                      <div className="nms-empty">{localizationStrings?.noData}</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {visible.length > 0 && (
            <div className="pagination-container">
              <div className="pagination-info">
                <p>
                  {localizationStrings?.showing} {(currentPage - 1) * PAGE_SIZE + 1}-
                  {Math.min(currentPage * PAGE_SIZE, visible.length)} {localizationStrings?.of}{" "}
                  {visible.length} {localizationStrings?.records}
                </p>
              </div>
              <div className="pagination">
                <button className="prev-btn" onClick={() => setPage(1)} disabled={currentPage === 1}>
                  {localizationStrings?.firstPage}
                </button>
                <button
                  className="prev-btn"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  {localizationStrings?.previous}
                </button>
                <button
                  className="next-btn"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  {localizationStrings?.next}
                </button>
                <button
                  className="last-btn"
                  onClick={() => setPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  {localizationStrings?.lastPage}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <div className="modal fade" id="deleteVlan" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content inc-modal">
            <div className="modal-body success-msg">
              <img src={errorIcon} alt="" />
              <p>
                {localizationStrings?.deleteConfirm} {vlanId} VLAN?
              </p>
              <button
                className="text-btn primary-btn"
                data-bs-dismiss="modal"
                onClick={() => deleteVlan(vlanId)}
              >
                {localizationStrings?.yes}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VlanNetworkSetup;
